import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Bot, CheckCircle2, Copy, ExternalLink, Loader2, ShieldCheck, Unlink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/lib/supabase";
import { useTelegramAccounts } from "@/lib/hooks/useTelegramAccounts";
import { buildBotDeepLink } from "@/lib/connections";
import { CardShell, LoadError, Notice } from "./shared";
import type { TelegramAccount } from "@/lib/types";

// Bot pessoal: vincula a conta do Telegram do usuário ao tenant com um token de
// uso único (create_telegram_link_code, 15 min) aberto por deep link
// t.me/<bot>?start=<token>. O status vira "Conectado" sozinho via Realtime.

export const TELEGRAM_BOT_USERNAME =
  (import.meta.env["VITE_TELEGRAM_BOT_USERNAME"] as string | undefined) ?? "LucraLinkBot";

const COMMANDS: ReadonlyArray<{ command: string; description: string }> = [
  { command: "/meuslinks", description: "seus últimos links com o total de cliques" },
  { command: "/enviar <link>", description: "converte o link e envia aos seus destinos" },
  { command: "/copy <link>", description: "gera um texto de venda com IA" },
];

// L4: lista TODAS as contas vinculadas, cada uma com "Desvincular". Antes só a
// primeira aparecia e as demais continuavam podendo usar os comandos sem o
// usuário ver. Mais de uma conta é legítimo (ex.: pessoal e da equipe).
export function TelegramBotCard(): JSX.Element {
  const accountsQuery = useTelegramAccounts();
  const accounts = (accountsQuery.data ?? []).filter((a) => a.verified_at);
  const linked = accounts.length > 0;
  return (
    <CardShell
      id="card-telegram-bot"
      icon={Bot}
      title="Comandos no Telegram"
      badge={
        <Badge variant={linked ? "success" : "muted"} className="ml-auto whitespace-nowrap">
          {linked ? "Conectado" : "Não conectado"}
        </Badge>
      }
    >
      <p className="text-sm text-muted-foreground">
        Use o @{TELEGRAM_BOT_USERNAME} no seu chat privado para consultar links e disparar ofertas sem abrir o painel.
      </p>
      <ul className="space-y-1.5 rounded-lg bg-[hsl(var(--surface-raised)/0.6)] px-4 py-3 text-sm">
        {COMMANDS.map((c) => (
          <li key={c.command} className="text-muted-foreground">
            <span className="font-mono text-foreground">{c.command}</span> - {c.description}
          </li>
        ))}
      </ul>
      {accountsQuery.isError ? (
        <LoadError onRetry={() => void accountsQuery.refetch()} retrying={accountsQuery.isFetching} />
      ) : (
        <>
          {linked && <LinkedAccounts accounts={accounts} />}
          <LinkAccount label={linked ? "Vincular outra conta" : "Conectar"} />
        </>
      )}
    </CardShell>
  );
}

function LinkedAccounts({ accounts }: { accounts: TelegramAccount[] }): JSX.Element {
  const queryClient = useQueryClient();
  const unlink = useMutation<void, Error, string>({
    mutationFn: async (accountId) => {
      const { error } = await supabase.rpc("unlink_telegram_account", { p_account_id: accountId });
      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["telegram-accounts"] });
    },
  });
  const confirmUnlink = (account: TelegramAccount): void => {
    if (window.confirm("Desvincular esta conta? Ela deixa de usar os comandos do bot.")) {
      unlink.mutate(account.id);
    }
  };
  return (
    <div className="space-y-2">
      <ul className="space-y-2">
        {accounts.map((account) => (
          <li key={account.id} className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <ShieldCheck size={15} className="text-primary" aria-hidden="true" />
            <span className="font-mono">{account.username ? `@${account.username}` : account.telegram_user_id}</span>
            <span>vinculado em {new Date(account.verified_at ?? account.created_at).toLocaleDateString("pt-BR")}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-auto gap-1.5 text-destructive hover:text-destructive"
              onClick={() => confirmUnlink(account)}
              disabled={unlink.isPending}
            >
              <Unlink size={14} />
              Desvincular
            </Button>
          </li>
        ))}
      </ul>
      {unlink.error && <Notice tone="danger">Não foi possível desvincular agora. Tente de novo.</Notice>}
    </div>
  );
}

function LinkAccount({ label }: { label: string }): JSX.Element {
  const [code, setCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const deepLink = code ? buildBotDeepLink(TELEGRAM_BOT_USERNAME, code) : null;

  const connect = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    setCopied(false);
    const { data, error: rpcError } = await supabase.rpc("create_telegram_link_code");
    setLoading(false);
    if (rpcError || typeof data !== "string") {
      setCode(null);
      setError("Não foi possível gerar o link agora. Tente de novo.");
      return;
    }
    setCode(data);
    window.open(buildBotDeepLink(TELEGRAM_BOT_USERNAME, data), "_blank", "noopener,noreferrer");
  };

  const copyCommand = async (): Promise<void> => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(`/start ${code}`);
      setCopied(true);
    } catch {
      setError("Não foi possível copiar. Selecione o comando e copie manualmente.");
    }
  };

  return (
    <div className="space-y-3">
      <Button type="button" variant="secondary" onClick={() => void connect()} disabled={loading}>
        {loading ? <Loader2 size={15} className="animate-spin" /> : <Bot size={15} />}
        {label}
      </Button>
      {error && <Notice tone="danger">{error}</Notice>}
      {deepLink && code && (
        <div className="space-y-3 rounded-lg border border-[hsl(var(--border-strong))] bg-[hsl(var(--surface-raised))] p-4">
          <p className="text-sm text-muted-foreground">
            Abrimos o Telegram em outra aba. Toque em <strong className="text-foreground">Iniciar</strong> na conversa com o
            bot. Se não abriu, use o link ou envie o comando abaixo.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <a href={deepLink} target="_blank" rel="noopener noreferrer">
                <ExternalLink size={14} />
                Abrir no Telegram
              </a>
            </Button>
            <code className="rounded bg-[hsl(var(--surface))] px-2 py-1 font-mono text-sm text-foreground">/start {code}</code>
            <Button type="button" size="icon" variant="ghost" aria-label="Copiar comando" onClick={() => void copyCommand()}>
              {copied ? <CheckCircle2 size={15} /> : <Copy size={15} />}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">O link vale por 15 minutos e só pode ser usado uma vez.</p>
        </div>
      )}
    </div>
  );
}
